import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemKind } from '../../shared/problem-text';
import type {
  AudiencePreview,
  AudienceSpec,
  Group,
  Person,
  PersonPage,
  Role,
  Site,
} from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { AudiencePicker, Checkbox, SearchField, Select, type AudienceChip } from '../admin-kit';
import { useSession } from '../../shared/session';
import {
  audienceIncomplete,
  describedAudience,
  previewOrNothing,
} from '../../shared/audience-preview';
import './communicatie.css';

/**
 * De doelgroepkiezer in de vorm van het nieuwe beheer, voor agenda, documenten, formulieren en
 * snelkoppelingen (BHR-5).
 *
 * De kit tekent alleen de weergave (segment, blauw vlak, chips); deze component doet wat de oude
 * kiezer in `communication/audience-picker.tsx` deed: groepen, vestigingen en rollen kiezen, en het
 * aantal komt van de server, met een korte vertraging. Een eigen kopie en geen verbouwing van die
 * oude kiezer, want het berichtenscherm (BHR-4) gebruikt hem en wordt tegelijk herontworpen.
 */
export function AudienceField({
  value,
  onChange,
  testId,
}: {
  value: AudienceSpec;
  onChange: (spec: AudienceSpec) => void;
  testId?: string;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [choosing, setChoosing] = useState(false);
  const [search, setSearch] = useState('');

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/groups');
      return ((data as { items: Group[] } | undefined)?.items ?? []) as Group[];
    },
  });

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/sites', {});
      return ((data as { items: Site[] } | undefined)?.items ?? []) as Site[];
    },
  });

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles');
      return ((data as { roles: Role[] } | undefined)?.roles ?? []) as Role[];
    },
  });

  // Het aantal komt van de server, zoals in de oude kiezer: zelf tellen zou iets anders zeggen
  // dan er straks gebeurt.
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), 400);
    return () => window.clearTimeout(timer);
  }, [value]);

  const preview = useQuery({
    queryKey: ['audience-preview', debounced],
    queryFn: () =>
      previewOrNothing(debounced, async () => {
        const { data, error } = await api.POST('/api/v1/audience/preview', {
          body: { audience: debounced } as never,
        });
        // Zonder recht op het voorbeeld zeggen we dat, in plaats van "nu niet op te halen" (FBK-2):
        // dat las als een storing, en de tester zocht de fout bij de doelgroep.
        if (problemKind(error) === 'denied') return 'denied' as const;
        if (error || !data) return null;
        return data as AudiencePreview;
      }),
  });

  const selectedGroups = value.type === 'filter' ? (value.groups ?? []) : [];
  const selectedRoles = value.type === 'filter' ? (value.roles ?? []) : [];
  const selectedSites = value.type === 'filter' ? (value.sites ?? []) : [];
  // FBK-6: losse personen en de soort medewerker (Stap 31), naast groepen, rollen en vestigingen.
  const selectedPersons = value.type === 'filter' ? (value.persons ?? []) : [];
  const selectedTypes = value.type === 'filter' ? (value.personTypes ?? []) : [];
  const groupMatch = value.type === 'filter' ? value.groupMatch : 'any';
  // Met subgroepen (FBK-5): standaard aan voor een nieuwe keuze. Een opgeslagen doelgroep zonder
  // de vlag stond uit, en blijft uit tot iemand het vinkje zet.
  const includeSubgroups = value.type === 'filter' ? value.includeSubgroups : undefined;

  /** Houdt de vorm geldig: een filter zonder keuzes bestaat niet, dat wordt "iedereen". */
  const setFilter = (next: {
    groups?: string[];
    roles?: string[];
    sites?: string[];
    groupMatch?: 'any' | 'all';
    includeSubgroups?: boolean;
    persons?: string[];
    personTypes?: PersonType[];
  }) => {
    const groupIds = next.groups ?? selectedGroups;
    const roleIds = next.roles ?? selectedRoles;
    const siteIds = next.sites ?? selectedSites;
    const personIds = next.persons ?? selectedPersons;
    const types = next.personTypes ?? selectedTypes;
    if (
      groupIds.length === 0 &&
      roleIds.length === 0 &&
      siteIds.length === 0 &&
      personIds.length === 0 &&
      types.length === 0
    ) {
      onChange({ type: 'all' });
      return;
    }
    onChange({
      type: 'filter',
      groups: groupIds,
      roles: roleIds,
      sites: siteIds,
      persons: personIds,
      personTypes: types,
      groupMatch: next.groupMatch ?? groupMatch,
      includeSubgroups:
        next.includeSubgroups ?? includeSubgroups ?? (value.type === 'all' ? true : undefined),
    });
  };

  // Personen zoeken (FBK-6): pas vanaf twee tekens, met de zoekterm van de kiezer.
  const term = search.trim();
  const personHits = useQuery({
    queryKey: ['audience-people', term],
    enabled: value.type === 'filter' && term.length >= 2,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/people', {
        params: { query: { search: term, status: 'active', limit: 6 } } as never,
      });
      return ((data as PersonPage | undefined)?.items ?? []) as Person[];
    },
  });
  // De namen van gekozen personen; ook voor een opgeslagen doelgroep die opnieuw opent.
  const personNames = useQuery({
    queryKey: ['audience-person-names', selectedPersons],
    enabled: selectedPersons.length > 0,
    queryFn: async () => {
      const found = await Promise.all(
        selectedPersons.map(async (personId) => {
          const { data } = await api.GET('/api/v1/people/{personId}', {
            params: { path: { personId } },
          });
          const person = data as Person | undefined;
          return [personId, person ? `${person.firstName} ${person.lastName}` : '…'] as const;
        }),
      );
      return new Map(found);
    },
  });

  const toggle = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((item) => item !== id) : [...list, id];

  const needle = search.trim().toLowerCase();
  const matches = (name: string) => needle === '' || name.toLowerCase().includes(needle);

  // Vestigingen eerst en alleen bij meer dan één (Stap 29): de grofste grens leest het eerst.
  const options = useMemo(() => {
    const siteList = (sites.data?.length ?? 0) > 1 ? (sites.data ?? []) : [];
    return [
      ...siteList.map((site) => ({ kind: 'site' as const, id: site.id, label: site.name })),
      ...(groups.data ?? []).map((group) => ({
        kind: 'group' as const,
        id: group.id,
        label: group.name,
        count: group.memberCount,
      })),
      ...(roles.data ?? []).map((role) => ({
        kind: 'role' as const,
        id: role.id,
        label: roleName(role),
      })),
    ];
  }, [sites.data, groups.data, roles.data]);

  const nameOf = (kind: 'site' | 'group' | 'role', id: string) =>
    options.find((option) => option.kind === kind && option.id === id)?.label ?? '…';

  const chips: AudienceChip[] = [
    ...selectedSites.map((id) => ({
      key: `site-${id}`,
      label: nameOf('site', id),
      onRemove: () => setFilter({ sites: selectedSites.filter((item) => item !== id) }),
    })),
    ...selectedGroups.map((id) => ({
      key: `group-${id}`,
      label: nameOf('group', id),
      onRemove: () => setFilter({ groups: selectedGroups.filter((item) => item !== id) }),
    })),
    ...selectedRoles.map((id) => ({
      key: `role-${id}`,
      label: nameOf('role', id),
      onRemove: () => setFilter({ roles: selectedRoles.filter((item) => item !== id) }),
    })),
    ...selectedTypes.map((type) => ({
      key: `type-${type}`,
      label: t(`audiencePersons.type.${type}`),
      onRemove: () => setFilter({ personTypes: selectedTypes.filter((item) => item !== type) }),
    })),
    ...selectedPersons.map((id) => ({
      key: `person-${id}`,
      label: personNames.data?.get(id) ?? '…',
      onRemove: () => setFilter({ persons: selectedPersons.filter((item) => item !== id) }),
    })),
  ];

  const isSelected = (kind: 'site' | 'group' | 'role', id: string) =>
    (kind === 'site' ? selectedSites : kind === 'group' ? selectedGroups : selectedRoles).includes(
      id,
    );

  const choose = (kind: 'site' | 'group' | 'role', id: string) => {
    if (kind === 'site') setFilter({ sites: toggle(selectedSites, id) });
    else if (kind === 'group') setFilter({ groups: toggle(selectedGroups, id) });
    else setFilter({ roles: toggle(selectedRoles, id) });
  };

  const summary = preview.isPending ? (
    t('common.loading')
  ) : preview.data && preview.data !== 'denied' ? (
    <>
      <strong>{t('posts.audience.count', { count: preview.data.count })}</strong>
      {describedAudience(preview.data.description)}
      {preview.data.sample.length > 0
        ? `. ${preview.data.sample.map((person) => person.displayName).join(', ')}${
            preview.data.count > preview.data.sample.length ? ' …' : ''
          }`
        : null}
    </>
  ) : preview.data === 'denied' ? (
    t('posts.audience.denied')
  ) : (
    t('posts.audience.unknown')
  );

  const kindLabel = {
    site: t('sites.title'),
    group: t('groups.title'),
    role: t('roles.title'),
  };
  const shown = options.filter((option) => matches(option.label));

  return (
    <div className="c-audience" data-testid={testId}>
      <AudiencePicker
        mode={value.type === 'filter' ? 'pick' : 'all'}
        onModeChange={(mode) => {
          if (mode === 'all') {
            setChoosing(false);
            onChange({ type: 'all' });
          } else {
            setChoosing(true);
            onChange({
              type: 'filter',
              groups: [],
              roles: [],
              sites: [],
              groupMatch: 'any',
              includeSubgroups: true,
            } as AudienceSpec);
          }
        }}
        summary={summary}
        chips={chips}
        onAdd={() => setChoosing((current) => !current)}
        addLabel={t('beheer.communicatie.audience.add')}
        error={audienceIncomplete(value) ? t('posts.audience.empty') : undefined}
        testId={testId ? `${testId}-picker` : undefined}
      />

      {value.type === 'filter' && choosing ? (
        <div className="c-audience-choose">
          <SearchField
            label={t('posts.audience.search')}
            placeholder={t('posts.audience.searchHint')}
            value={search}
            onChange={setSearch}
          />
          {shown.length === 0 ? (
            <p className="a-hint">{t('posts.audience.noGroups')}</p>
          ) : (
            (['site', 'group', 'role'] as const).map((kind) => {
              const list = shown.filter((option) => option.kind === kind);
              if (list.length === 0) return null;
              return (
                <fieldset key={kind} className="c-audience-group">
                  <legend className="a-sect-title">{kindLabel[kind]}</legend>
                  <div className="a-actions">
                    {list.map((option) => {
                      const active = isSelected(kind, option.id);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={active}
                          className={cn('c-audience-option', active && 'on')}
                          onClick={() => choose(kind, option.id)}
                        >
                          {option.label}
                          {'count' in option ? ` (${option.count})` : ''}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })
          )}
          {term.length >= 2 ? (
            <fieldset className="c-audience-group" data-testid="audience-people">
              <legend className="a-sect-title">{t('audiencePersons.people')}</legend>
              <div className="a-actions">
                {(personHits.data ?? []).length === 0 ? (
                  <span className="a-hint">{t('audiencePersons.noPeople')}</span>
                ) : (
                  (personHits.data ?? []).map((person) => {
                    const active = selectedPersons.includes(person.id);
                    return (
                      <button
                        key={person.id}
                        type="button"
                        aria-pressed={active}
                        data-testid="audience-person-option"
                        className={cn('c-audience-option', active && 'on')}
                        onClick={() => setFilter({ persons: toggle(selectedPersons, person.id) })}
                      >
                        {person.firstName} {person.lastName}
                        {person.employeeNumber ? ` · ${person.employeeNumber}` : ''}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="a-hint">{t('audiencePersons.peopleHint')}</div>
            </fieldset>
          ) : null}
          <fieldset className="c-audience-group">
            <legend className="a-sect-title">{t('audiencePersons.types')}</legend>
            <div className="a-actions">
              {PERSON_TYPES.map((type) => {
                const active = selectedTypes.includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={active}
                    className={cn('c-audience-option', active && 'on')}
                    onClick={() =>
                      setFilter({
                        personTypes: active
                          ? selectedTypes.filter((item) => item !== type)
                          : [...selectedTypes, type],
                      })
                    }
                  >
                    {t(`audiencePersons.type.${type}`)}
                  </button>
                );
              })}
            </div>
            <div className="a-hint">{t('audiencePersons.typesHint')}</div>
          </fieldset>
          {/*
            Alleen als een gekozen groep subgroepen heeft: anders is de vraag ruis (FBK-5).
          */}
          {selectedGroups.some((id) =>
            (groups.data ?? []).some((group) => group.parentGroupId === id),
          ) ? (
            <Checkbox
              testId={testId ? `${testId}-subgroups` : 'audience-subgroups'}
              checked={includeSubgroups === true}
              onChange={(on) => setFilter({ includeSubgroups: on })}
              label={t('groupTree.includeSubgroups')}
              hint={t('groupTree.includeSubgroupsHint')}
            />
          ) : null}
          {selectedGroups.length > 1 ? (
            <div className="a-field">
              <label className="a-label" htmlFor={`${testId ?? 'audience'}-match`}>
                {t('posts.audience.groupMatch')}
              </label>
              <Select
                id={`${testId ?? 'audience'}-match`}
                value={groupMatch}
                onChange={(event) => setFilter({ groupMatch: event.target.value as 'any' | 'all' })}
              >
                <option value="any">{t('posts.audience.groupMatchAny')}</option>
                <option value="all">{t('posts.audience.groupMatchAll')}</option>
              </Select>
              <div className="a-hint">{t('posts.audience.groupMatchHint')}</div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type PersonType = 'employee' | 'temporary' | 'contractor';
const PERSON_TYPES: PersonType[] = ['employee', 'temporary', 'contractor'];

/** De rolnaam in de taal van de gebruiker, met terugval op wat er wél staat. */
function roleName(role: Role): string {
  const names = role.name as Record<string, string | undefined>;
  return names.nl ?? names.en ?? names.fr ?? Object.values(names).find(Boolean) ?? '—';
}
